import type { JSX } from 'preact';
import { forwardRef } from 'preact/compat';
import type { EdgeTabShape } from '@feedback/shared';

/** The dialog's id. `aria-controls` on the tab points here. One widget per page. */
export const EDGE_PANEL_ID = 'ft-panel';

const TIP_ID = 'ft-tab-tip';

const CONVERSATION_PATH = 'M1 2 L23 2 L23 16 L11 16 L5 23 L5 16 L1 16 Z';
/** SPOG `close`. The modal's close control and the phone dock's open state use it. */
export const CLOSE_PATH =
  'M3.4 1 L12 9.6 L20.6 1 L23 3.4 L14.4 12 L23 20.6 L20.6 23 L12 14.4 L3.4 23 L1 20.6 L9.6 12 L1 3.4 Z';

const VARIANT_CLASS: Record<EdgeTabShape['variant'], string> = {
  SLANT: 'ft-tab--slant',
  SQUARE_ENDS: 'ft-tab--square',
  NOTCH: 'ft-tab--notch',
  COMPACT: 'ft-tab--compact',
};

export interface EdgeTabProps {
  open: boolean;
  /** Keyboard up, the host called `hide()`, or the modal is open. Stops the tab taking taps. */
  hidden?: boolean;
  /** Plays the entrance once, then the caller clears it. */
  entering?: boolean;
  onToggle: () => void;
  /** The application's launcher label. Also the accessible name, so the visible word is in it. */
  label: string;
  /** How the application's shape and display mode draw on an edge (`resolveEdgeTab`). */
  tab: EdgeTabShape;
  /** The application's chosen icon, already drawn in `currentColor`; null keeps the tab's own glyph. */
  icon: JSX.Element | null;
  /** Tooltip copy, or null when the application turned the tooltip off. */
  tooltip: string | null;
}

/**
 * The fixed right-edge Feedback tab, painted from the application's Appearance
 * settings like the button is (I22656, ADR 0036).
 *
 * A native button. The silhouette is on an inner box, and the label is the
 * only thing rotated, so the word stays upright. The button itself is the 44px
 * hit area and is not clipped. Colors arrive as `--fb-*` properties set on
 * `.ft-root` by `App`; the shape arrives as a variant class plus, for a radius
 * or a mask, an inline style on the shape box.
 */
export const EdgeTab = forwardRef<HTMLButtonElement, EdgeTabProps>(function EdgeTab(
  { open, hidden, entering, onToggle, label, tab, icon, tooltip },
  ref,
) {
  const iconOnly = tab.showIcon && !tab.showLabel && tab.variant !== 'COMPACT';
  const className = [
    'ft-tab',
    VARIANT_CLASS[tab.variant],
    iconOnly ? 'ft-tab--icon-only' : '',
    entering ? 'is-entering' : '',
    hidden ? 'is-hidden' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      ref={ref}
      type="button"
      class={className}
      aria-expanded={open ? 'true' : 'false'}
      aria-controls={EDGE_PANEL_ID}
      aria-label={label}
      aria-describedby={tooltip ? TIP_ID : undefined}
      onClick={onToggle}
    >
      <span class="ft-tab__frame">
        <span class="ft-tab__shape" style={tab.shapeStyle}>
          {tab.showIcon ? (
            <span class="ft-tab__icon">
              {icon ?? (
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path d={CONVERSATION_PATH} />
                </svg>
              )}
            </span>
          ) : null}
          {tab.showLabel ? <span class="ft-tab__label">{label}</span> : null}
        </span>
      </span>
      {tooltip ? (
        <span id={TIP_ID} class="ft-tab__tip" role="tooltip">
          {tooltip}
        </span>
      ) : null}
    </button>
  );
});

/** The tab's own glyph, for a docked trigger when the application chose no icon. */
export function DockIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d={CONVERSATION_PATH} />
    </svg>
  );
}

/** The glyph's path, for the `mountTrigger` button, which is built outside Preact. */
export const DOCK_ICON_MARKUP = `<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false"><path fill="currentColor" d="${CONVERSATION_PATH}"/></svg>`;
